import { DisplayText, displayText } from '@kikkz/shared/i18n/util';
import { firstGoalToDialValue } from '../labels/first-goal';
import { formatMatchMinute } from '../labels/match-minute-format';
import { AdditionalQuestionType } from '../labels/fixture-enum-labels';
import { Fixture, FixtureMatchStats, GameScoreValues } from '../models/fixture-models';

/**
 * Max points / display suffix per bonus-question type. Total over all 7
 * {@link AdditionalQuestionType} members because the title/info dictionaries also
 * cover the 3 main cards (MATCH_RESULT/FIRST_GOAL_SCORER/LINEUP) — those, like the
 * original switch's `default` branch, simply get the uniform 30/'' fallback.
 */
export const ADDITIONAL_QUESTION_CONFIG: Record<AdditionalQuestionType, { max: number; suffix: '' | '%' }> = {
  MATCH_RESULT: { max: 30, suffix: '' },
  FIRST_GOAL_SCORER: { max: 30, suffix: '' },
  LINEUP: { max: 30, suffix: '' },
  SHOTS_ON_GOAL: { max: 40, suffix: '' },
  CORNERS: { max: 20, suffix: '' },
  MATCH_MINUTE_OF_FIRST_GOAL: { max: 30, suffix: '' },
  BALL_POSSESSION: { max: 30, suffix: '%' },
};

/**
 * Per-type lookup used only by the "real" bonus questions (`additionalPossiblePoints`,
 * `correctAdditionalDisplay`, `additionalQuestionInitialValue`/`ADDITIONAL_QUESTION_FIELD`).
 * Deliberately partial — MATCH_RESULT/FIRST_GOAL_SCORER/LINEUP/MATCH_MINUTE_OF_FIRST_GOAL
 * (the last handled separately via the dial) have no entry here, exactly matching the
 * original switches' `default: null` / no-op behavior for those types.
 */
const ADDITIONAL_QUESTION_LOOKUP: Partial<
  Record<AdditionalQuestionType, { pointsKey: keyof GameScoreValues; statsField: keyof FixtureMatchStats }>
> = {
  SHOTS_ON_GOAL: { pointsKey: 'maxScoreShotsOnGoal', statsField: 'shotsOnGoal' },
  CORNERS: { pointsKey: 'maxScoreCorners', statsField: 'corners' },
  MATCH_MINUTE_OF_FIRST_GOAL: { pointsKey: 'maxScoreMatchMinuteOfFirstGoal', statsField: 'matchMinuteOfFirstGoal' },
  BALL_POSSESSION: { pointsKey: 'maxScoreBallPossession', statsField: 'ballPossessionPercentage' },
};

/** Field on `FixtureMatchStats` that stores the answer for a bonus question, keyed by its type.
 *  MATCH_MINUTE_OF_FIRST_GOAL is deliberately absent — it goes through the dial value mapping
 *  (`dialValueToFirstGoal`/`firstGoalToDialValue`), not through a single stats field. */
export const ADDITIONAL_QUESTION_FIELD: Partial<Record<AdditionalQuestionType, keyof FixtureMatchStats>> = {
  SHOTS_ON_GOAL: 'shotsOnGoal',
  CORNERS: 'corners',
  BALL_POSSESSION: 'ballPossessionPercentage',
};

/** Slider/dial max for a bonus question (defaults to 30 when `type` is null/undefined,
 *  matching the pre-fixture-load state of `selectedFixture()?.additionalQuestionType`). */
export function additionalQuestionMax(type: AdditionalQuestionType | undefined | null): number {
  return type ? ADDITIONAL_QUESTION_CONFIG[type].max : 30;
}

/** Slider/dial value suffix for a bonus question (`'%'` for possession, else `''`). */
export function additionalQuestionSuffix(type: AdditionalQuestionType | undefined | null): '' | '%' {
  return type ? ADDITIONAL_QUESTION_CONFIG[type].suffix : '';
}

/** Counts how many of the (up to 4) prediction cards already have a saved answer for `fixture`. */
export function countPredictedCards(fixture: Fixture | null | undefined): number {
  const pred = fixture?.userPrediction;
  if (!pred) return 0;

  let count = 0;
  if (pred.resultTeam != null && pred.resultOpponent != null) count++;
  if (pred.firstGoalScorer || pred.firstGoalScorerNoGoal) count++;
  if (pred.lineup && pred.lineup.length > 0) count++;

  const type = fixture.additionalQuestionType;
  if (
    (type === 'SHOTS_ON_GOAL' && pred.shotsOnGoal != null) ||
    (type === 'CORNERS' && pred.corners != null) ||
    (type === 'MATCH_MINUTE_OF_FIRST_GOAL' && pred.firstGoalType != null) ||
    (type === 'BALL_POSSESSION' && pred.ballPossessionPercentage != null)
  )
    count++;

  return count;
}

/**
 * Lineup max points scale with how many players the user has currently chosen:
 * look the count up in the by-player-count table (e.g. 11 chosen → 20). Falls back
 * to the highest entry whose key ≤ count, so a sparse table still resolves sensibly.
 * Null while nothing is selected or before the scoring config has loaded.
 */
export function lineupPossiblePoints(
  byCount: Record<string, number> | undefined | null,
  selectedCount: number,
): number | null {
  if (!byCount) return null;
  if (selectedCount <= 0) return null;

  const exact = byCount[String(selectedCount)];
  if (exact != null) return exact;

  // No exact key: pick the value for the largest count that does not exceed the selection.
  let fallback: number | null = null;
  let bestCount = -1;
  for (const [count, points] of Object.entries(byCount)) {
    const n = Number(count);
    if (n <= selectedCount && n > bestCount) {
      bestCount = n;
      fallback = points;
    }
  }
  return fallback;
}

/** Possible first-goal-scorer points: for the picked player they scale with their position;
 *  before a player is chosen we show the best achievable (max across positions) so the
 *  "Mögliche Punkte: X" label always carries a number. Null only until the config loads. */
export function goalScorerPossiblePoints(
  values: GameScoreValues | undefined | null,
  prediction: FixtureMatchStats | null | undefined,
): number | null {
  if (prediction?.firstGoalScorerNoGoal) return values?.maxScoreFirstGoalScorerNoGoal ?? null;
  const byPosition = values?.maxScoreFirstGoalScorerByPosition;
  if (!byPosition) return null;
  const player = prediction?.firstGoalScorer;
  if (player) return byPosition[player.position] ?? null;
  const positionValues = Object.values(byPosition);
  return positionValues.length ? Math.max(...positionValues) : null;
}

/** Additional-question max points depend on the fixture's question type. */
export function additionalPossiblePoints(
  values: GameScoreValues | undefined | null,
  type: AdditionalQuestionType | undefined | null,
): number | null {
  if (!values || !type) return null;
  const entry = ADDITIONAL_QUESTION_LOOKUP[type];
  if (!entry) return null;
  const points = values[entry.pointsKey];
  return typeof points === 'number' ? points : null;
}

/** Formatted "correct result" (home-left/away-right display order) once the match has stats. */
export function correctResultDisplay(fixture: Fixture | null | undefined): DisplayText | null {
  const stats = fixture?.matchStats;
  if (stats?.resultTeam == null || stats?.resultOpponent == null) return null;
  // resultTeam/resultOpponent are FC-relative; display order follows home (left) / away (right).
  return displayText(
    fixture?.teamIsHome ? `${stats.resultTeam}:${stats.resultOpponent}` : `${stats.resultOpponent}:${stats.resultTeam}`,
  );
}

/** Formatted "correct answer" for the fixture's bonus question, once match stats exist. */
export function correctAdditionalDisplay(fixture: Fixture | null | undefined): DisplayText | null {
  if (!fixture?.matchStats) return null;
  const stats = fixture.matchStats;
  switch (fixture.additionalQuestionType) {
    case 'SHOTS_ON_GOAL':
      return stats.shotsOnGoal != null ? displayText(String(stats.shotsOnGoal)) : null;
    case 'CORNERS':
      return stats.corners != null ? displayText(String(stats.corners)) : null;
    case 'MATCH_MINUTE_OF_FIRST_GOAL':
      return stats.matchMinuteOfFirstGoal != null ? formatMatchMinute(stats.matchMinuteOfFirstGoal) : null;
    case 'BALL_POSSESSION':
      return stats.ballPossessionPercentage != null ? displayText(`${stats.ballPossessionPercentage}%`) : null;
    default:
      return null;
  }
}

/** Current dial/slider value to preselect for the fixture's bonus question, if any. */
export function additionalQuestionInitialValue(fixture: Fixture | null | undefined): number | null {
  if (!fixture) return null;
  const pred = fixture.userPrediction;
  switch (fixture.additionalQuestionType) {
    case 'SHOTS_ON_GOAL':
      return pred?.shotsOnGoal ?? null;
    case 'CORNERS':
      return pred?.corners ?? null;
    // The minute predictor works on the dial ordinal (0 = kein Tor, 46 = 45'+, 92 = 90'+).
    case 'MATCH_MINUTE_OF_FIRST_GOAL':
      return firstGoalToDialValue(pred?.firstGoalType, pred?.matchMinuteOfFirstGoal);
    case 'BALL_POSSESSION':
      return pred?.ballPossessionPercentage ?? null;
    default:
      return null;
  }
}

/** Builds the `FixtureMatchStats` patch to send to the backend for `fixture`. */
export function buildPredictionPatch(
  fixture: Fixture | null | undefined,
  patch: Partial<FixtureMatchStats>,
): FixtureMatchStats | null {
  if (!fixture) return null;

  const current = fixture.userPrediction;
  return {
    fixtureId: fixture.id,
    resultTeam: current?.resultTeam,
    resultOpponent: current?.resultOpponent,
    firstGoalScorer: current?.firstGoalScorer,
    firstGoalScorerNoGoal: current?.firstGoalScorerNoGoal,
    lineup: current?.lineup,
    shotsOnGoal: current?.shotsOnGoal,
    corners: current?.corners,
    firstGoalType: current?.firstGoalType,
    matchMinuteOfFirstGoal: current?.matchMinuteOfFirstGoal,
    ballPossessionPercentage: current?.ballPossessionPercentage,
    // The patch spreads last, so an explicit `matchMinuteOfFirstGoal: undefined` in the
    // patch (e.g. switching from IN_REGULAR_TIME to 45'+) overrides the stored minute.
    ...patch,
  };
}
